import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal } from '@angular/cdk/portal';
import {
	ComponentRef,
	DestroyRef,
	Directive,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	TemplateRef,
	untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { CommonClientService } from '~ng-common-lib/cdk/common-client.service';

import { TooltipMessageComponent } from './tooltip-message.component';

@Directive({
	selector: '[tooltip]',
	standalone: true,
})
export class TooltipDirective implements OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly platform = inject(Platform);
	private readonly overlayService = inject(Overlay);
	private readonly commonClientService = inject(CommonClientService);

	private readonly supportsMouseEvents = !this.platform.IOS && !this.platform.ANDROID;

	public readonly $tooltipShowDelay = input<number>(200);
	public readonly $tooltipHideDelay = input<number>(200);
	public readonly $tooltipStationaryDelay = input<number>(350, {
		alias: 'tooltipStationaryDelay',
	});

	public readonly $tooltip = input.required<null | string | TemplateRef<void>>({
		alias: 'tooltip',
	});
	public readonly $tooltipContext = input<Record<string, unknown>>(
		{},
		{ alias: 'tooltipContext' }
	);
	public readonly $tooltipOnMobile = input(false, {
		alias: 'tooltipOnMobile',
	});

	private readonly portal = new ComponentPortal(TooltipMessageComponent);

	private overlay?: OverlayRef;
	private ref?: ComponentRef<TooltipMessageComponent>;

	private delayTimeout?: number;
	private _eventId = 0;
	private isHovering = false;

	constructor() {
		effect(() => {
			const message = this.$tooltip();
			const context = this.$tooltipContext();

			untracked(() => {
				this.update(message, context);
			});
		});

		if (this.supportsMouseEvents) {
			fromEvent(this.elementRef.nativeElement, 'mouseenter')
				.pipe(takeUntilDestroyed())
				.subscribe({
					next: () => {
						this.isHovering = true;
						this.scheduleShowWhenStationary();
					},
				});

			fromEvent(this.elementRef.nativeElement, 'mousemove')
				.pipe(takeUntilDestroyed())
				.subscribe({
					next: () => {
						if (this.overlay?.hasAttached()) {
							this.overlay.detach();
						}

						this.scheduleShowWhenStationary();
					},
				});

			fromEvent(this.elementRef.nativeElement, 'mouseleave')
				.pipe(takeUntilDestroyed())
				.subscribe({
					next: () => {
						this.isHovering = false;
						this.hide();
					},
				});
		}

		effect((cleanUpFn) => {
			const tooltipOnMobile = this.$tooltipOnMobile();

			if (tooltipOnMobile) {
				const subEnter = fromEvent(this.elementRef.nativeElement, 'touchstart')
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: (event) => {
							event.preventDefault();
							this.show();
						},
					});

				const subLeave = fromEvent(this.elementRef.nativeElement, 'touchend')
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: () => {
							this.hide();
						},
					});

				cleanUpFn(() => {
					subEnter.unsubscribe();
					subLeave.unsubscribe();
				});
			} else {
				this.isHovering = false;
				this.hide();
			}
		});
	}

	private scheduleShowWhenStationary(): void {
		this.executeWithDelay(this.$tooltipStationaryDelay(), () => {
			if (!this.isHovering) {
				return;
			}

			this.show(0);
		});
	}

	ngOnDestroy(): void {
		this._eventId++;
		this.overlay?.dispose();
		this.ref?.destroy();
	}

	private show(delay = this.$tooltipShowDelay()): void {
		this.executeWithDelay(delay, () => {
			const message = this.$tooltip();
			if (!this.hasContent(message)) {
				this.overlay?.detach();
				return;
			}

			const overlay = this.getOverlay();

			overlay.updatePositionStrategy(
				this.overlayService
					.position()
					.flexibleConnectedTo(this.elementRef)
					.withPositions([
						// 1) Below the element, aligned to the left
						{
							originX: 'start',
							originY: 'bottom',
							overlayX: 'start',
							overlayY: 'top',
						},
						// 2) Above the element, aligned to the left
						{
							originX: 'start',
							originY: 'top',
							overlayX: 'start',
							overlayY: 'bottom',
						},
						// 3) Below the element, aligned to the right (tooltip content shifts left)
						{
							originX: 'end',
							originY: 'bottom',
							overlayX: 'end',
							overlayY: 'top',
						},
						// 4) Above the element, aligned to the right (tooltip content shifts left)
						{
							originX: 'end',
							originY: 'top',
							overlayX: 'end',
							overlayY: 'bottom',
						},
					])
					.withFlexibleDimensions(false)
					.withPush(false)
			);

			overlay.detach();

			this.ref = overlay.attach(this.portal);
			this.ref.instance.setMessage(message, this.$tooltipContext());
		});
	}

	private hide(): void {
		this.executeWithDelay(this.$tooltipHideDelay(), () => {
			this.overlay?.detach();
		});
	}

	private update(
		message: string | TemplateRef<void> | null,
		context: Record<string, unknown>
	): void {
		if (!this.hasContent(message)) {
			this.overlay?.detach();
			return;
		}

		this.ref?.instance.setMessage(message, context);
	}

	private hasContent(message: string | TemplateRef<void> | null): boolean {
		if (message instanceof TemplateRef) {
			return true;
		}

		return typeof message === 'string' && message.trim().length > 0;
	}

	private getOverlay(): OverlayRef {
		this.overlay ??= this.overlayService.create({
			hasBackdrop: false,
			panelClass: 'pointer-events-none',
		});

		return this.overlay;
	}

	private executeWithDelay(delay: number, cb: () => void): void {
		const id = ++this._eventId;

		if (this.commonClientService.isBrowser) {
			window.clearTimeout(this.delayTimeout);

			this.delayTimeout = window.setTimeout(() => {
				if (id === this._eventId) {
					cb();
				}
			}, delay);
		}
	}
}
